import React, { useState } from 'react';
import { message, Modal } from 'antd';
import BulkUploadEditor from './BulkUploadEditor';
import StepOneMapping from './StepOneMapping';
import { useSpin } from '../../../components/SpinContext';
import { useToken } from '../../../common/helper';
import { columnMatching } from '../../../apis/shipment';

interface FieldMapping {
  header_name: string;
  actual_name: string;
  enable: boolean;
}

interface DataMappingModalProps {
  showMappingModal: boolean;
  setShowMappingModal: (show: boolean) => void;
  setFieldMappings: (
    mappings: FieldMapping[] | ((prev: FieldMapping[]) => FieldMapping[])
  ) => void;
  setModalOpen: (open: boolean) => void;
  fileKey: string;
  fieldMappings: FieldMapping[];
  unauthorize: () => void;
}

interface FileDataItem {
  [key: string]: unknown;
}

/**
 * DataMappingModal - A two-step wizard modal for bulk shipment data mapping and validation
 *
 * This modal provides a workflow for users to:
 * 1. Map CSV column headers to system field names (Step 1)
 * 2. Preview and edit the mapped data before final submission (Step 2)
 *
 * @param showMappingModal - Controls modal visibility
 * @param setShowMappingModal - Function to control modal visibility
 * @param setFieldMappings - Function to update field mappings state
 * @param setModalOpen - Function to control parent modal state
 * @param fileKey - Unique identifier for the uploaded file
 * @param fieldMappings - Array of field mapping objects with header names and system names
 * @param unauthorize - Function to handle unauthorized access scenarios
 */
const DataMappingModal: React.FC<DataMappingModalProps> = ({
  showMappingModal,
  setShowMappingModal,
  setFieldMappings,
  setModalOpen,
  fileKey,
  fieldMappings,
  unauthorize,
}) => {
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [step, setStep] = useState<number>(1);
  const { showSpin, hideSpin } = useSpin();
  const [fileData, setFileData] = useState<FileDataItem[]>([]);
  const { token } = useToken();

  const blurActiveElement = (): void => {
    const ae = document.activeElement;
    if (ae instanceof HTMLElement) {
      ae.blur();
    }
  };

  const handleToggle = (index: number, checked: boolean): void => {
    const field = fieldMappings[index];
    const updatedField = { ...field, enable: checked };

    setFieldMappings((prev) =>
      prev.map((f, i) => (i === index ? updatedField : f))
    );
  };

  const handleMappingChange = (index: number, newActualName: string): void => {
    setFieldMappings((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, actual_name: newActualName } : field
      )
    );
  };

  const handleNext = (): void => {
    if (step === 1) {
      const cleanedPayload = {
        key: fileKey,
        mapping: fieldMappings
          .filter(
            (field) =>
              field.actual_name &&
              field.header_name &&
              field.actual_name.trim() !== '' &&
              field.header_name.trim() !== ''
          )
          .map((field) => ({
            header_name: field.header_name,
            actual_name: field.actual_name,
            enable: field.enable !== false,
          })),
      };

      showSpin();
      columnMatching(token, cleanedPayload)
        .then((res) => {
          const data = res?.data?.data || null;
          if (data?.is_success) {
            message.success(data?.message);

            setFileData(data?.data);
            setStep(2);
          } else {
            message.error(
              data?.message || 'Submission failed. Please try again.'
            );
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          hideSpin();
        });
    } else {
      blurActiveElement();
      setShowMappingModal(false);
    }
  };

  return (
    <Modal
      title={null}
      centered
      open={showMappingModal}
      onCancel={() => {
        blurActiveElement();
        setShowMappingModal(false);
        setStep(1);
      }}
      width='96vw'
      footer={null}
      className='bulk-main-modal'
      styles={{
        body: {
          borderRadius: '18px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 32px)',
          height: 'auto',
          flex: 1,
          minHeight: 0,
          padding: 2,
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
        }}
      >
        {step === 1 ? (
          <StepOneMapping
            mapping={mapping}
            setMapping={setMapping}
            fieldMappings={fieldMappings}
            onCancel={() => {
              blurActiveElement();
              setModalOpen(false);
              setShowMappingModal(false);
              setStep(1);
            }}
            onNext={handleNext}
            handleToggle={handleToggle}
            handleMappingChange={handleMappingChange}
          />
        ) : (
          <BulkUploadEditor
            fileData={fileData}
            onCancel={() => {
              blurActiveElement();
              setModalOpen(false);
              setShowMappingModal(false);
              setStep(1);
            }}
            unauthorize={unauthorize}
            onBackToStep1={() => setStep(1)}
          />
        )}
      </div>
    </Modal>
  );
};

export default DataMappingModal;
